
//import { Transform as KonvaTransform } from 'konva/lib/Util'
import { normalize3 } from './operation';
export class Transform {

  // m[0,0] is -1 because we want a classic geometric plane, so positive to the right and to the top, negative to the left and to the bottom
  // and the regular canvas works like positive to the right and to the bottom, negative to the left and to the top
  //
  //      /|\                                    |
  //       |                                     |
  //       |        \                            |          \
  //----------------       instead of   ---------------------
  //       |        /                            |          /
  //       |                                     |
  //       |                                    \|/

  m: number[][];
  public static readonly mat5Identity = [
    [1, 0, 0, 0],
    [0, 1, 0, 0],
    [0, 0, 1, 0],
    [0, 0, 0, 1]]
  public static readonly mat5FlippedIdentity = [
    [1, 0, 0, 0],
    [0, -1, 0, 0],
    [0, 0, 1, 0],
    [0, 0, 0, 1]]
  constructor(
    m = [
      [1, 0, 0, 0],
      [0, 1, 0, 0],
      [0, 0, 1, 0],
      [0, 0, 0, 1]]) {

    this.m = new Array(4).fill(0).map(() => new Array(4));

    this.m[0][0] = m[0][0];
    this.m[0][1] = m[0][1];
    this.m[0][2] = m[0][2];
    this.m[0][3] = m[0][3];

    this.m[1][0] = m[1][0];
    this.m[1][1] = m[1][1];
    this.m[1][2] = m[1][2];
    this.m[1][3] = m[1][3];

    this.m[2][0] = m[2][0];
    this.m[2][1] = m[2][1];
    this.m[2][2] = m[2][2];
    this.m[2][3] = m[2][3];

    this.m[3][0] = m[3][0];
    this.m[3][1] = m[3][1];
    this.m[3][2] = m[3][2];
    this.m[3][3] = m[3][3];
  }

  reset() {
    this.m = [
      [1, 0, 0, 0],
      [0, 1, 0, 0],
      [0, 0, 1, 0],
      [0, 0, 0, 1]];
  }
  static translationTransform(x = 0, y = 0, z = 0) {
    return new Transform([
      [1, 0, 0, 0],
      [0, 1, 0, 0],
      [0, 0, 1, 0],
      [x, y, z, 1]])
  }
  static rotationTransform(angleRad: number, axis: { x: number, y: number, z: number } = { x: 0, y: 0, z: 1 }) {
    const n = normalize3(axis);
    const c = Math.cos(angleRad);
    const s = Math.sin(angleRad);
    //Rodrigues' rotation formula
    return new Transform([
      [
        c * (1 - n.x * n.x) + n.x * n.x,
        n.x * n.y * (1 - c) + n.z * s,
        n.x * n.z * (1 - c) - n.y * s,
        0
      ],
      [
        n.x * n.y * (1 - c) - n.z * s,
        c * (1 - n.y * n.y) + n.y * n.y,
        n.y * n.z * (1 - c) + n.x * s,
        0
      ],
      [
        n.x * n.z * (1 - c) + n.y * s,
        n.y * n.z * (1 - c) - n.x * s,
        c * (1 - n.z * n.z) + n.z * n.z,
        0
      ],
      [0, 0, 0, 1]])
  }
  static scaleTransform(x: number = 1, y: number = 1, z: number = 1) {
    return new Transform([
      [x, 0, 0, 0],
      [0, y, 0, 0],
      [0, 0, z, 0],
      [0, 0, 0, 1]]);
  }

  copy() {
    return new Transform(this.m);
  }

  copyInto(tr: Transform) {
    tr.m = [...this.m];
  }

  multiply(tr: Transform) {
    const m = this.m;
    //console.log("mult");

    const n: number[][] = new Array(4).fill(0).map(() => new Array(4).fill(0));

    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        for (let k = 0; k < 4; k++) {
          n[i][j] += m[i][k] * tr.m[k][j];
        }
      }
    }
    this.m = n;
    return this;
  }

  getMatrix() {
    return this.m;
  }
  getMatrix2d() {
    return [
      this.m[0][0],
      this.m[0][1],
      this.m[1][0],
      this.m[1][1],
      this.m[3][0],
      this.m[3][1],
    ]
  }

  getTranslation() {
    return {
      x: this.m[3][0],
      y: this.m[3][1],
      z: this.m[3][2],
    };
  }

  point2d(point: { x: number, y: number, z?: number }): Point2d {
    const m = this.m;

    const x = point.x * m[0][0] + point.y * m[1][0] + m[3][0];
    const y = point.x * m[0][1] + point.y * m[1][1] + m[3][1];
    return {
      x: x,
      y: y
    };
  }

  invert2d() {
    const d = 1 / (this.m[0][0] * this.m[1][1] - this.m[0][1] * this.m[1][0]);
    const m0 = this.m[1][1] * d;
    const m1 = -this.m[0][1] * d;
    const m2 = -this.m[1][0] * d;
    const m3 = this.m[0][0] * d;
    const m4 = d * (this.m[1][0] * this.m[3][1] - this.m[1][1] * this.m[3][0]);
    const m5 = d * (this.m[0][1] * this.m[3][0] - this.m[0][0] * this.m[3][1]);

    this.m[0][0] = m0;
    this.m[0][1] = m1;
    this.m[0][2] = 0;
    this.m[0][3] = 0;
    this.m[1][0] = m2;
    this.m[1][1] = m3;
    this.m[1][2] = 0;
    this.m[1][3] = 0;
    this.m[2][0] = 0;
    this.m[2][1] = 0;
    this.m[2][2] = 1;
    this.m[2][3] = 0;
    this.m[3][0] = m4;
    this.m[3][1] = m5;
    this.m[3][2] = 0;
    this.m[3][3] = 1;
    return this;
  }
  getRotation() {
    let a = this.m[0][0];
    let b = this.m[0][1];
    let c = this.m[1][0];
    let d = this.m[1][1];
    let result = 0;
    if (a !== 0 || b !== 0) {
      let r = Math.sqrt(a * a + b * b);
      result = b > 0 ? Math.acos(a / r) : -Math.acos(a / r);
    } else if (c !== 0 || d !== 0) {
      let s = Math.sqrt(c * c + d * d);
      result =
        Math.PI / 2 - (d > 0 ? Math.acos(-c / s) : -Math.acos(c / s));
    }
    return result;
  }

  translate(x: number = 0, y: number = 0, z: number = 0) {
    this.multiply(Transform.translationTransform(x, y, z))
    return this;
  }
  rotate(angleRad: number, axis?: { x: number, y: number, z: number }) {
    this.multiply(Transform.rotationTransform(angleRad, axis))
    return this;
  }
  scale(x: number = 1, y: number = 1, z: number = 1) {
    this.multiply(Transform.scaleTransform(x, y, z))
    return this;
  }
  flip() {
    this.multiply(new Transform(Transform.mat5FlippedIdentity))
    return this;
  }
}


